import React from 'react';
import cx from 'classnames';

interface LabeledValuePillLayoutConfig {
    /**
     * The message to show for a given label or value
     */
    message: string;
    /**
     * Min width to allow for a given label or value.
     */
    minWidth?: number;
    /**
     * Max width to allow for a given label or value. Content beyond this width will be truncated.
     */
    maxWidth?: number;
}

interface LabeledValuePillProps {
    config: {
        label: LabeledValuePillLayoutConfig;
        value: LabeledValuePillLayoutConfig;
    };
    /**
     * A size used to scale the text and padding.
     * @defaultValue 'xs'
     */
    textSize?: 'xs' | 'sm';
}

/**
 * Renders a label / value pair with a rounded border. The label has a slightly darker background,
 * thereby highlighting the value's importance.
 * @param props {@link LabeledValuePillProps}
 * @returns
 */
export const LabeledValuePill = ({
    config,
    textSize = 'xs',
}: LabeledValuePillProps): JSX.Element => {
    const textClasses = cx({
        'py-1 px-2': textSize === 'xs',
        'py-2 px-3': textSize === 'sm',
        'text-xs': textSize === 'xs',
        'text-sm': textSize === 'sm',
    });

    return (
        <div className="inline-block border rounded border-tertiary-200">
            <div className="flex">
                <div
                    className={cx(
                        textClasses,
                        'border-r border-tertiary-200 bg-tertiary-100',
                        { truncate: config.label.maxWidth !== undefined },
                    )}
                    style={{
                        maxWidth: config.label.maxWidth,
                        minWidth: config.label.minWidth,
                    }}
                >
                    {config.label.message}
                </div>
                <div
                    className={cx(textClasses, {
                        truncate: config.value.maxWidth !== undefined,
                    })}
                    style={{
                        maxWidth: config.value.maxWidth,
                        minWidth: config.value.minWidth,
                    }}
                >
                    {config.value.message}
                </div>
            </div>
        </div>
    );
};
